import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    TextField,
    CircularProgress,
    Box,
    Typography,
    Tabs,
    Tab,
} from "@mui/material";
import _ from "lodash";
import moment from "moment";
import { categoriesApi } from "@api";
import type { IAccommodationCountry, IAccommodationState } from "@api";
import { useAppSelector } from "@redux";
import { ProductCard, DraggableProduct } from "./ProductCard";
import {
    mapToDraggable,
    extractProducts,
    RawSearchProduct,
} from "./productSidebarMapper";

// Re-exported for backwards-compatibility with existing tests that
// import the mapper from this module.
export { mapToDraggable };
export type { RawSearchProduct };

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    min-height: 0;
`;

const ResultsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    padding-right: 4px;
    flex: 1;
    min-height: 200px;
`;

const Empty = styled.div`
    color: #888;
    font-size: 13px;
    padding: 16px 4px;
`;

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

interface ProductSidebarProps {
    /**
     * Forwarded to each ProductCard. When set, tapping a card invokes this
     * callback (used by the mobile bottom sheet to show an "Add to which day?"
     * picker rather than requiring a touch-drag).
     */
    onSelectProduct?: (product: DraggableProduct) => void;
}

type TabKey = "tours" | "accommodation";

const ToursTab = ({ onSelectProduct }: ProductSidebarProps) => {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<DraggableProduct[]>([]);
    const [error, setError] = useState<string | null>(null);

    const debouncedSearch = useMemo(
        () =>
            _.debounce(async (q: string) => {
                setError(null);
                if (!q || q.trim().length < 2) {
                    setResults([]);
                    setLoading(false);
                    return;
                }
                setLoading(true);
                try {
                    const res = await categoriesApi.searchProducts({
                        q: q.trim(),
                        per_page: 20,
                        page: 1,
                    });
                    const mapped = extractProducts(res.data)
                        .map(mapToDraggable)
                        .filter((p): p is DraggableProduct => !!p && !!p.productPricesDetailsId);
                    setResults(mapped);
                } catch (e) {
                    console.error("Trip planner search failed", e);
                    setError("Search failed. Please try again.");
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, 350),
        [],
    );

    useEffect(() => {
        debouncedSearch(query);
        return () => debouncedSearch.cancel();
    }, [query, debouncedSearch]);

    return (
        <>
            <TextField
                size="small"
                placeholder="Search tours, activities, accommodation..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                fullWidth
            />
            <ResultsList>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                {!loading && error && <Empty>{error}</Empty>}
                {!loading && !error && results.length === 0 && (
                    <Empty>
                        {query.trim().length >= 2
                            ? "No products found."
                            : "Type at least 2 characters to search."}
                    </Empty>
                )}
                {!loading &&
                    results.map((p) => (
                        <ProductCard
                            key={p.productId}
                            product={p}
                            onSelect={onSelectProduct}
                        />
                    ))}
            </ResultsList>
        </>
    );
};

const AccommodationTab = ({ onSelectProduct }: ProductSidebarProps) => {
    // Trip start date drives the default check-in/out for the
    // accommodation search. The wt endpoint requires both dates even
    // when the user just wants to "browse hotels in Cairns"; we send
    // start..start+1 (one night) as a sensible default. Users can
    // refine nights via the per-item edit modal after dropping.
    const tripStartDate = useAppSelector((s) => s.tripPlanner.current.startDate);

    // The accommodation backend has its OWN id namespace (accommCountryId
    // / accommStateId), distinct from the TDMS location tree the other
    // tabs use. We must source these from the dedicated lookups, never
    // the cached location tree — passing TDMS ids returns no results.
    const [countries, setCountries] = useState<IAccommodationCountry[]>([]);
    const [states, setStates] = useState<IAccommodationState[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);

    const [countryId, setCountryId] = useState<string>("");
    const [stateId, setStateId] = useState<string>("");
    const [query, setQuery] = useState("");

    // Fetch the accommodation country list once on mount.
    useEffect(() => {
        let active = true;
        setLoadingCountries(true);
        categoriesApi
            .getAccommodationCountries()
            .then((res) => {
                if (active) setCountries(res.data ?? []);
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.error("[Accommodation] failed to load countries", e);
                if (active) setCountries([]);
            })
            .finally(() => {
                if (active) setLoadingCountries(false);
            });
        return () => {
            active = false;
        };
    }, []);

    // Fetch the "state" (city/region-level) list whenever the selected
    // accommodation country changes.
    useEffect(() => {
        if (!countryId) {
            setStates([]);
            setLoadingStates(false);
            return;
        }
        let active = true;
        setStates([]);
        setLoadingStates(true);
        categoriesApi
            .getAccommodationStates(countryId)
            .then((res) => {
                if (active) setStates(res.data ?? []);
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.error("[Accommodation] failed to load states", e);
                if (active) setStates([]);
            })
            .finally(() => {
                if (active) setLoadingStates(false);
            });
        return () => {
            active = false;
        };
    }, [countryId]);

    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<DraggableProduct[]>([]);
    const [error, setError] = useState<string | null>(null);

    const debouncedSearch = useMemo(
        () =>
            _.debounce(
                async (args: {
                    countryId: string;
                    stateId: string;
                    q: string;
                    tripStart: string;
                }) => {
                    setError(null);
                    // The availableAccommodationProductsByLocation endpoint
                    // REQUIRES both a country and a state (destination) — a
                    // country-only request 400s. So we only search once both
                    // are picked. The keyword can't be sent server-side (the
                    // endpoint rejects `q`), so it's applied client-side below.
                    const hasInput = args.countryId && args.stateId;
                    if (!hasInput) {
                        setResults([]);
                        setLoading(false);
                        return;
                    }
                    setLoading(true);
                    try {
                        // Default check-in to today if the trip's start date
                        // is missing/invalid, check-out is +1 day.
                        const start = moment(args.tripStart, "YYYY-MM-DD", true).isValid()
                            ? moment(args.tripStart, "YYYY-MM-DD")
                            : moment();
                        const res = await categoriesApi.searchAccommodation({
                            countries: args.countryId || undefined,
                            states: args.stateId || undefined,
                            limit: 20,
                            checkInDate: start.format("YYYY-MM-DD"),
                            checkOutDate: start.clone().add(1, "day").format("YYYY-MM-DD"),
                        });
                        // The wt accommodation endpoint already returns
                        // accommodation-only rows, but the mapper still
                        // normalises the class (some payloads come back
                        // with the "A" code) so downstream trip planner
                        // code keys off `productClass === "accommodation"`
                        // consistently.
                        const kw = args.q.trim().toLowerCase();
                        const mapped = extractProducts(res.data)
                            .map(mapToDraggable)
                            .filter(
                                (p): p is DraggableProduct =>
                                    !!p && !!p.productPricesDetailsId,
                            )
                            // Keyword filtering is client-side only (the
                            // endpoint rejects a `q` param). Match on the
                            // product name, case-insensitively.
                            .filter((p) => !kw || p.name.toLowerCase().includes(kw));
                        setResults(mapped);
                    } catch (e) {
                        // Surface the actual failure to both the console and
                        // the user-visible Empty slot. The wt accommodation
                        // endpoint commonly fails with 401 (token interceptor
                        // didn't attach a shared/agent token) or 400 (bad
                        // location id). Without the details the message
                        // "Search failed. Please try again." gave us nothing
                        // to act on.
                        const ax = e as {
                            message?: string;
                            response?: { status?: number; data?: unknown };
                            config?: { url?: string; baseURL?: string };
                        };
                        const status = ax.response?.status;
                        const url = `${ax.config?.baseURL ?? ""}${ax.config?.url ?? ""}`;
                        // eslint-disable-next-line no-console
                        console.error("[Accommodation] search failed", {
                            status,
                            url,
                            responseData: ax.response?.data,
                            message: ax.message,
                            error: e,
                        });
                        const detail =
                            status !== undefined
                                ? `HTTP ${status}`
                                : ax.message ?? "unknown error";
                        setError(`Search failed (${detail}). See browser console for details.`);
                        setResults([]);
                    } finally {
                        setLoading(false);
                    }
                },
                350,
            ),
        [],
    );

    useEffect(() => {
        debouncedSearch({ countryId, stateId, q: query, tripStart: tripStartDate });
        return () => debouncedSearch.cancel();
    }, [countryId, stateId, query, tripStartDate, debouncedSearch]);

    return (
        <>
            <Controls>
                {/* Native selects so mobile gets the OS picker, and so
                    location values are reachable via standard form
                    semantics in tests. Country/State here are the
                    accommodation backend's own ids (accommCountryId /
                    accommStateId), NOT the TDMS location-tree ids. */}
                <TextField
                    size="small"
                    select
                    label="Country"
                    value={countryId}
                    onChange={(e) => {
                        setCountryId(e.target.value);
                        setStateId("");
                    }}
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "aria-label": "Country" }}
                    disabled={loadingCountries && countries.length === 0}
                >
                    <option value="">Any country</option>
                    {countries.map((c) => (
                        <option key={c.accommCountryId} value={c.accommCountryId}>
                            {c.accommCountryName}
                        </option>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    select
                    label="Destination"
                    value={stateId}
                    onChange={(e) => setStateId(e.target.value)}
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "aria-label": "Destination" }}
                    disabled={!countryId || loadingStates || states.length === 0}
                >
                    <option value="">Any destination</option>
                    {states.map((s) => (
                        <option key={s.accommStateId} value={s.accommStateId}>
                            {s.accommStateName}
                        </option>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    placeholder="Keyword (optional)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    fullWidth
                />
            </Controls>
            <ResultsList>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                {!loading && error && <Empty>{error}</Empty>}
                {!loading && !error && results.length === 0 && (
                    <Empty>
                        {countryId && stateId
                            ? "No places to stay found."
                            : "Pick a country and destination to see places to stay."}
                    </Empty>
                )}
                {!loading &&
                    results.map((p) => (
                        <ProductCard
                            key={p.productId}
                            product={p}
                            onSelect={onSelectProduct}
                        />
                    ))}
            </ResultsList>
        </>
    );
};

export const ProductSidebar = ({ onSelectProduct }: ProductSidebarProps = {}) => {
    const [tab, setTab] = useState<TabKey>("tours");

    return (
        <Wrapper>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Add products</Typography>
            <Tabs
                value={tab}
                onChange={(_e, v: TabKey) => setTab(v)}
                variant="fullWidth"
                sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, fontSize: 13, textTransform: "none" } }}
            >
                <Tab value="tours" label="Tours & Activities" />
                <Tab value="accommodation" label="Accommodation" />
            </Tabs>
            {/* Both tabs stay mounted so each retains its own scroll
                position, query, and selection while the user flips
                between them. CSS-hide rather than unmount the inactive
                tab. */}
            <div style={{ display: tab === "tours" ? "contents" : "none" }}>
                <ToursTab onSelectProduct={onSelectProduct} />
            </div>
            <div style={{ display: tab === "accommodation" ? "contents" : "none" }}>
                <AccommodationTab onSelectProduct={onSelectProduct} />
            </div>
        </Wrapper>
    );
};
